import * as React from 'react';
import { graphql, HeadFC } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import { MDXProvider } from '@mdx-js/react';
import { Components } from '@mdx-js/react/lib';

import Box from '@mui/joy/Box';
import Container from '@mui/joy/Container';
import Typography from '@mui/joy/Typography';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as mdxFragments from '../utils/graphql';
import Video from '../components/video';
import { SEO } from '../components/seo';
import VTag, { TagQuery } from '../components/videos/Tag';
import { Social } from 'gatsby-theme-youtube/src/components/layout/nav/primary';

interface YoutubePost {
  id: string;
  excerpt: string;
  slug: string;
  title: string;
  category: {
    name: string;
    slug: string;
  };
  date: string;
  seoDate: string;
  ogImage: any;
  tags: TagQuery[];
  series?: string;
  season?: number;
  episode?: number;
  youtubeId: string;
  keywords: string[];
}

export type YoutubeDataProps = {
  post: YoutubePost;
  previous: any;
  next: any;
  all: { nodes: any[] };

  mdxContent: string;
};

export const Head: HeadFC<YoutubeDataProps> = ({ data: { post } }) => (
  <SEO
    title={post.title}
    pathname={post.slug}
    description={post.description || post.excerpt}
    image={getSrc(post.ogImage)}
  >
    {/* <meta name="twitter:label1" value="Length" />
    <meta name="twitter:data1" value={`${post.duration}`} /> */}
    <meta name="twitter:label2" value="Category" />
    <meta name="twitter:data2" value={post.category.name} />
    <meta name="article:published_time" content={post.seoDate} />
  </SEO>
);

const YoutubeView: React.FC<React.PropsWithChildren<YoutubeDataProps>> = ({
  post = {} as YoutubePost,
  children,
  mdxContent,
}) => {
  const { date, title, series, season, episode } = post;
  const seriesText =
    series && season && episode
      ? `${series}\u00A0\u00A0S${season} • Episode ${episode}`
      : '';

  const { tags } = post;
  const Tag = ({ id }: { id: string }) => {
    const tag = tags.find((x) => x.name == id) || ({} as TagQuery);
    return <VTag {...tag} sx={{ mr: 2, mt: 1 }} />;
  };
  const components: Components = {
    Tag,
    Social,
  };

  return (
    <Container
      component="main"
      sx={{
        mt: (theme) => theme.getCssVar('toolbar-height'),
        mb: { xs: 2, sm: 3 },
      }}
    >
      <Box component="article" className="yt-video">
        <Video {...post} />

        <header>
          {seriesText && (
            <Typography
              level="body-sm"
              sx={{
                lineHeight: 1,
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              {seriesText}
            </Typography>
          )}
          <Typography level="h4">{title}</Typography>
          <div className="row yt-meta">
            {date && (
              <Typography gutterBottom level="body-sm">
                {date}
              </Typography>
            )}
            {/* <div className="flex" /> */}
            {/* <PostShare post={post} path={path} /> */}
          </div>
        </header>

        {children}
        <MDXProvider components={components}>{mdxContent}</MDXProvider>

        {/* <footer>
        <PostComments path={path} />
      </footer> */}
      </Box>
    </Container>
  );
};

export default YoutubeView;

export const query = graphql`
  fragment YtFull on YoutubePost {
    ...YtInit
    date(formatString: "MMMM DD, YYYY")
    seoDate: date(formatString: "YYYY-MM-DD")
    ogImage: cover {
      ...OgImage
    }
    ...YtSeries
    youtubeId
    keywords
  }

  fragment OgImage on File {
    childImageSharp {
      gatsbyImageData(
        layout: FIXED
        width: 1200
        height: 630
        transformOptions: { cropFocus: CENTER }
      )
    }
  }
`;
